import { Form, Formik } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FILTER_DOORS } from "../../../../constants/notification-key";
import { COMMAND_SETTING_ID, DEFAULT_WIDGET } from "../../../../constants/widget-constants";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { Widget } from "../../../../models/Widget";
import WizardSideBarStep from "../../../../models/WizardSideBarStep";
import { useGlobalState } from "../../../../Providers";
import { getErrorMessage } from "../../../../services/utilities/ServiceError";
import { storeWidget } from "../../../../services/widgetService";
import { IAppState } from "../../../../store/types";
import { useDialog } from "../../../dialog/DialogContext";
import Wizard from "../../../wizard/wizard";
import { getCommandkey } from "../commandUtil";
import CommandTypeStep from "./command-type-step";
import ItemSelectStep from "./item-select-step";
import SummaryStep from "./summary-step";

interface Props {
    onClose: () => void;
}

const CommandWizard: FC<Props> = ({ onClose }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(0);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const dialog = useDialog();
    const initialSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.type),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.selectedType),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
    ];
    const [steps, setSteps] = useState<WizardSideBarStep[]>(initialSteps);
    const initialItem = {
        filterBy: FILTER_DOORS.eSysnameLookup,
        filterById: FILTER_DOORS.sdValstring,
        configurationName: '',
        configurationId: 0,
        configurationType: '',
    }

    const onLocalNext = () => {
        setStep(step + 1);
    };

    const onLocalBack = (event: any) => {
        if (step > 0) {
            setStep(step - 1);
        }
    };

    const handleClose = () => {
        setStep(0);
        setSteps(initialSteps);
        onClose();
    }

    const setSideBarStepDescription = (index, newDescription) => {
        setSteps(prevState => {

            if (index === 0) {
                const newState = [...prevState];
                newState[index + 1].title = newDescription;
                newState[index].description = newDescription;
                return newState;
            }
            const newState = [...prevState];
            newState[index].description = newDescription;
            return newState;
        });
    };

    const content: ReactElement[] = [
        <CommandTypeStep
            onBack={onLocalBack}
            onNext={onLocalNext}
            setSideBarStepDescription={function (discription: string): void {
                setSideBarStepDescription(0, discription);
            }}
        />,
        step === 1 ?
            <ItemSelectStep
                onBack={onLocalBack}
                onNext={onLocalNext}
                setSideBarStepDescription={(discription) => {
                    setSideBarStepDescription(1, discription);
                }}
            />
            : <></>,
        step === 2 ? <SummaryStep
            onBack={onLocalBack} /> :
            <></>,
    ];

    const { setRefreshDashboard } = useGlobalState() ?? {};

    const storeWidgetData = async (values) => {
        try {
            const widget: Widget = {
                x: DEFAULT_WIDGET.X_POSITION,
                y: DEFAULT_WIDGET.Y_POSITION,
                width: 0,
                height: 0,
                type: values.filterBy,
                settingIdToCopy: COMMAND_SETTING_ID.toString(),
                userLoginId: user.id,
                titleBackgroundColor: DEFAULT_WIDGET.TITLE_BKGCOLOR,
                titleForegroundColor: DEFAULT_WIDGET.TITLE_FGCOLOR,
                id: 0,
                relationships: [
                    {
                        "eSysnameLookup": values.filterBy, //READER_COMMANDS,CONTROLLER_COMMANDS
                        "sdValstring": values.configurationId,
                        [getCommandkey(values.filterBy)]: values.configurationId,
                    }
                ]
            }
            await storeWidget(widget);
            setRefreshDashboard && setRefreshDashboard(true);
            toast.success(translate.byKey(TranslationKeys.success));
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            handleClose();
        }
    }

    return <>
        <Formik
            enableReinitialize={true}
            initialValues={initialItem}
            onSubmit={storeWidgetData}>
            <Form>
                <Wizard
                    name={translate.byKey(TranslationKeys.commands)}
                    step={step}
                    onClose={handleClose}
                    sidebarSteps={steps}>
                    {content}
                </Wizard>
            </Form>
        </Formik>
    </>
}

export default CommandWizard;